import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Columns2,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileUp,
  ShieldCheck,
  Wallet,
  Upload,
  Mail as MailIcon,
  Phone as PhoneIcon,
  Plus as PlusIcon,
  RefreshCw,
  Scale,
  ScrollText,
  Search,
  Timer,
  User as UserIcon
} from 'lucide-react';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useJob } from '../context/JobContext';
import api from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  backgroundImage: 'none',
  backgroundColor: theme.palette.background.paper
}));

const UI_COLORS = {
  indigo: '#4f46e5',
  sky: '#0ea5e9',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444'
};

const translateStato = (value) => {
  const map = { aperto: 'Aperto', in_lavorazione: 'In lavorazione', chiuso: 'Chiuso', annullato: 'Annullato' };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const StatoChip = ({ stato }) => {
  const key = String(stato || '').toLowerCase();
  const color = key === 'chiuso' ? UI_COLORS.green : key === 'annullato' ? UI_COLORS.red : key === 'in_lavorazione' ? UI_COLORS.amber : UI_COLORS.indigo;
  return (
    <Chip
      label={translateStato(key) || '-'}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 700, color, borderColor: color, textTransform: 'capitalize' }}
    />
  );
};

const translateIniziativa = (value) => {
  const map = { cliente: 'Cliente', studio: 'Società/Studio' };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const getDossierStatusLabel = (status) => {
  if (!status) return '';
  const map = {
    documents_sent: 'Documenti inviati',
    failed: 'Fallito',
    'in negotiation': 'In trattativa',
    processing: 'In lavorazione',
    submitted_quote: 'Preventivo inviato',
    terminated: 'Terminato'
  };
  return map[status] || status;
};

const formatMoneyIt = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n === 0) return '-';
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
};

const IniziativaChip = ({ value }) => {
  const key = String(value || '').toLowerCase();
  const color = key === 'studio' ? UI_COLORS.indigo : UI_COLORS.sky;
  return (
    <Chip
      label={translateIniziativa(key) || '-'}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 700, color, borderColor: color, textTransform: 'capitalize' }}
    />
  );
};

const translateOrigineCreazione = (value) => {
  const map = { manuale: 'Manuale', import_csv: 'Import CSV' };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const MotivazioneChip = ({ value }) => {
  const label = String(value || '').trim();
  return (
    <Chip
      label={label || '-'}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 700, maxWidth: 240 }}
    />
  );
};

const OrigineChip = ({ value }) => {
  const key = String(value || '').toLowerCase();
  const isImport = key === 'import_csv';
  const color = isImport ? UI_COLORS.amber : UI_COLORS.green;
  return (
    <Chip
      label={translateOrigineCreazione(key) || '-'}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 700, color, borderColor: color, textTransform: 'capitalize' }}
    />
  );
};

const formatDateIt = (value) => {
  const s = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '-';
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
};

const toIsoDateOnly = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const addDaysIso = (isoDate, days) => {
  const iso = String(isoDate || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const base = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(base.getTime())) return '';
  base.setUTCDate(base.getUTCDate() + Number(days || 0));
  return base.toISOString().slice(0, 10);
};

const formatDateTimeIt = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const formatNumberIt = (value) => {
  const n = Number(value || 0);
  return new Intl.NumberFormat('it-IT').format(Number.isFinite(n) ? n : 0);
};

const formatPercent1It = (value) => {
  const n = Number(value || 0);
  return new Intl.NumberFormat('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(Number.isFinite(n) ? n : 0);
};

const isValidEmail = (value) => {
  const s = String(value || '').trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
};

const escapeHtml = (value) => {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const buildVsPrevSubtitle = (deltaAbs, deltaPct) => {
  const dA = Number(deltaAbs || 0);
  const isNeg = dA < 0;
  const sign = dA > 0 ? '+' : '';
  const pct = deltaPct === null || deltaPct === undefined ? '—' : `${sign}${formatPercent1It(deltaPct)}`;
  return {
    text: `${sign}${formatNumberIt(dA)} (${pct}%) vs prec.`,
    color: isNeg ? UI_COLORS.red : UI_COLORS.green
  };
};

const StatWidget = ({ title, value, subtitle, subtitleColor, valueColor, icon: Icon, accent, onClick }) => (
  <Paper
    elevation={0}
    variant="outlined"
    onClick={onClick}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') onClick(e);
          }
        : undefined
    }
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      minHeight: 84,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 120ms ease, box-shadow 120ms ease',
      '&:hover': onClick
        ? {
            transform: 'translateY(-1px)',
            boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.22), 0 10px 18px -6px rgba(145, 158, 171, 0.18)'
          }
        : undefined
    }}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${accent}15`,
        color: accent,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 auto'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.2 }} noWrap>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.15, color: valueColor || 'inherit' }} noWrap>
        {value}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" sx={{ mt: 0.25, fontWeight: 800, color: subtitleColor || 'text.secondary' }} noWrap>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  </Paper>
);

const normalizeCustomerLabel = (customer) => {
  if (!customer) return '';
  const label = String(customer.full_name || '').trim();
  if (label) return label;
  if (customer.id) return `Cliente #${customer.id}`;
  return '';
};

const normalizeDossierLabel = (dossier) => {
  if (!dossier) return '';
  const ref = String(dossier.reference || '').trim();
  if (ref) return ref;
  if (dossier.id) return `Dossier #${dossier.id}`;
  return '';
};

export default function Recessi() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addJob } = useJob();
  const csvInputRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const isAdmin = String(user?.role_name || user?.role || '').toLowerCase() === 'admin';
  const [csvInfoOpen, setCsvInfoOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvImportFile, setCsvImportFile] = useState(null);
  const [csvImportTipo, setCsvImportTipo] = useState('cliente');
  const [csvImportMotivazioneId, setCsvImportMotivazioneId] = useState('');
  const [csvImportModalitaStudio, setCsvImportModalitaStudio] = useState('revoca');

  const columnDefs = useMemo(
    () => [
      { key: 'data_apertura', label: 'Data', sortable: true, align: 'left' },
      { key: 'stato', label: 'Stato', sortable: true, align: 'left' },
      { key: 'tipo_iniziativa', label: 'Iniziativa', sortable: true, align: 'left' },
      { key: 'motivazione_descrizione', label: 'Motivazione', sortable: true, align: 'left' },
      { key: 'id_cliente', label: 'Cliente', sortable: true, align: 'left' },
      { key: 'data_conferimento', label: 'Data Conferimento', sortable: true, align: 'left' },
      { key: 'importo_totale', label: 'Importo', sortable: true, align: 'right' },
      { key: 'acconto', label: 'Acconto', sortable: true, align: 'right' },
      { key: 'responsabili', label: 'Responsabili', sortable: true, align: 'left' },
      { key: 'dossier_reference', label: 'Dossier', sortable: true, align: 'left' },
      { key: 'creato_da_name', label: 'Creato da', sortable: true, align: 'left' },
      { key: 'note', label: 'Note', sortable: false, align: 'left' },
      { key: 'origine_creazione', label: 'Origine', sortable: true, align: 'left' }
    ],
    []
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [widgetsLoading, setWidgetsLoading] = useState(true);
  const [widgetsData, setWidgetsData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('data_apertura');
  const [filterText, setFilterText] = useState('');
  const [filterStato, setFilterStato] = useState('');
  const [filterIniziativa, setFilterIniziativa] = useState('');
  const [filterOrigine, setFilterOrigine] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([
    'data_apertura',
    'stato',
    'tipo_iniziativa',
    'motivazione_descrizione',
    'id_cliente',
    'data_conferimento',
    'importo_totale',
    'responsabili',
    'origine_creazione'
  ]);

  const [accessDialogOpen, setAccessDialogOpen] = useState(true);
  const [accessGranted, setAccessGranted] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [emailAccountsLoading, setEmailAccountsLoading] = useState(false);
  const [emailAccountId, setEmailAccountId] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: '', cc: '', subject: '', body: '' });

  const [customerById, setCustomerById] = useState({});
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerInputValue, setCustomerInputValue] = useState('');

  const [dossierById, setDossierById] = useState({});
  const [dossierOptions, setDossierOptions] = useState([]);
  const [dossierLoading, setDossierLoading] = useState(false);
  const [dossierInputValue, setDossierInputValue] = useState('');

  const [motivazioni, setMotivazioni] = useState([]);
  const [motivazioniLoading, setMotivazioniLoading] = useState(false);
  const [clientePreavvisoTouched, setClientePreavvisoTouched] = useState(false);

  const [formData, setFormData] = useState({
    id_contratto: '',
    id_cliente: '',
    tipo_iniziativa: 'cliente',
    note: '',
    cliente: {
      data_comunicazione_recesso: '',
      data_scadenza_preavviso: '',
      canale_comunicazione: 'pec',
      id_motivazione: '',
      motivazione_dettaglio: ''
    },
    studio: {
      modalita: 'revoca',
      data_comunicazione_studio: '',
      data_preavviso_cliente: '',
      id_motivazione: '',
      motivazione_dettaglio: ''
    }
  });

  const fetchRows = async () => {
    const seq = (fetchSeqRef.current += 1);
    setLoading(true);
    setWidgetsLoading(true);
    try {
      const listParams = {
        search: filterText || undefined,
        stato: filterStato || undefined,
        tipo_iniziativa: filterIniziativa || undefined,
        origine_creazione: filterOrigine || undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined
      };

      const widgetParams = {
        search: filterText || undefined,
        stato: filterStato || undefined,
        tipo_iniziativa: filterIniziativa || undefined,
        origine_creazione: filterOrigine || undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined
      };

      const [listRes, widgetsRes] = await Promise.allSettled([
        api.get('/recessi', { params: listParams }),
        api.get('/recessi/widgets', { params: widgetParams })
      ]);

      if (listRes.status === 'fulfilled') {
        if (seq === fetchSeqRef.current) setRows(Array.isArray(listRes.value.data) ? listRes.value.data : []);
      } else {
        if (seq === fetchSeqRef.current) setRows([]);
        toast.error('Errore nel caricamento dei recessi');
      }

      if (widgetsRes.status === 'fulfilled') {
        if (seq === fetchSeqRef.current) setWidgetsData(widgetsRes.value.data || null);
      } else {
        if (seq === fetchSeqRef.current) setWidgetsData(null);
      }
    } catch (err) {
      console.error('Error fetching recessi:', err);
      toast.error('Errore nel caricamento dei recessi');
      if (seq === fetchSeqRef.current) {
        setRows([]);
        setWidgetsData(null);
      }
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
        setWidgetsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!accessGranted) return undefined;
    const timeoutId = window.setTimeout(() => {
      setPage(0);
      fetchRows();
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [accessGranted, filterText, filterStato, filterIniziativa, filterOrigine, dateFrom, dateTo]);

  const handleAccessConfirm = () => {
    setAccessDialogOpen(false);
    setAccessGranted(true);
  };

  const handleAccessCancel = () => {
    setAccessDialogOpen(false);
    window.history.back();
  };

  const fetchEmailAccounts = async () => {
    setEmailAccountsLoading(true);
    try {
      const res = await api.get('/email-accounts/senders');
      const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      setEmailAccounts(list);
      return list;
    } catch (_) {
      setEmailAccounts([]);
      return [];
    } finally {
      setEmailAccountsLoading(false);
    }
  };

  const pickPreferredEmailAccountId = (accounts) => {
    const userEmail = String(user?.email || '').trim().toLowerCase();
    if (!userEmail) return '';
    const match = (accounts || []).find((a) => String(a?.email || '').trim().toLowerCase() === userEmail);
    return match?.id ? String(match.id) : '';
  };

  const buildDefaultEmailDraft = (row) => {
    const customer = row?.id_cliente ? customerById[String(row.id_cliente)] || null : null;
    const customerName = normalizeCustomerLabel(customer) || (row?.id_cliente ? `Cliente #${row.id_cliente}` : 'Cliente');
    const customerEmail = customer?.email ? String(customer.email).trim() : '';
    const dossier = row?.id_contratto ? dossierById[String(row.id_contratto)] || null : null;
    const dossierLabel = dossier?.reference ? String(dossier.reference).trim() : (row?.id_contratto ? `Dossier #${row.id_contratto}` : '');
    const openedAt = row?.data_apertura ? formatDateIt(row.data_apertura) : '';

    const subjectParts = [
      'Comunicazione recesso',
      row?.id_recesso ? `#${row.id_recesso}` : null,
      dossierLabel ? `- ${dossierLabel}` : null
    ].filter(Boolean);

    const bodyLines = [
      `Gentile ${customerName},`,
      '',
      'con la presente desideriamo aggiornarLa in merito alla gestione del recesso.',
      row?.id_recesso ? `Riferimento recesso: #${row.id_recesso}` : null,
      openedAt ? `Data apertura: ${openedAt}` : null,
      dossierLabel ? `Contratto (dossier): ${dossierLabel}` : null,
      '',
      'Restiamo a disposizione per eventuali chiarimenti.',
      '',
      'Cordiali saluti'
    ].filter((x) => x !== null);

    return {
      to: isValidEmail(customerEmail) ? customerEmail : '',
      cc: '',
      subject: subjectParts.join(' '),
      body: bodyLines.join('<br />')
    };
  };

  const openEmailDialog = async (row) => {
    setEmailTarget(row || null);
    const draft = buildDefaultEmailDraft(row);
    setEmailForm(draft);
    setEmailDialogOpen(true);

    let accounts = emailAccounts;
    if (accounts.length === 0) {
      accounts = await fetchEmailAccounts();
    }
    if (!Array.isArray(accounts) || accounts.length === 0) {
      toast.error('Nessuna casella email abilitata all’invio. Contatta un amministratore.');
      setEmailDialogOpen(false);
      setEmailTarget(null);
      return;
    }
    const preferredId = pickPreferredEmailAccountId(accounts);
    const selected = preferredId
      ? accounts.find((a) => String(a?.id) === String(preferredId))
      : (accounts[0] || null);
    if (selected?.id != null) {
      setEmailAccountId(String(selected.id));
    } else {
      setEmailAccountId('');
    }

    if (!draft.to) {
      toast.warning('Email cliente non disponibile: compila manualmente il destinatario');
    }
  };

  const closeEmailDialog = () => {
    if (emailSending) return;
    setEmailDialogOpen(false);
    setEmailTarget(null);
  };

  const handleSendEmail = async () => {
    if (!emailAccountId) {
      toast.error('Seleziona un account email per l’invio');
      return;
    }
    if (!isValidEmail(emailForm.to) || !String(emailForm.subject || '').trim() || !String(emailForm.body || '').trim()) {
      toast.error('Compila i campi obbligatori (A, Oggetto, Messaggio)');
      return;
    }

    setEmailSending(true);
    try {
      const payload = new FormData();
      payload.append('to', String(emailForm.to).trim());
      if (emailForm.cc) {
        payload.append('cc', String(emailForm.cc).trim());
      }
      payload.append('subject', String(emailForm.subject).trim());
      // For body we send a plain text version if needed, or just the HTML
      payload.append('body', String(emailForm.body).replace(/<[^>]+>/g, ''));
      payload.append('body_html', String(emailForm.body));

      await api.post(`/email-accounts/${emailAccountId}/send`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Email inviata con successo');
      closeEmailDialog();
    } catch (error) {
      const data = error?.response?.data || {};
      const traceId =
        data.traceId ||
        data.trace_id ||
        error?.response?.headers?.['x-trace-id'] ||
        error?.response?.headers?.['X-Trace-Id'];
      const code = data.code ? String(data.code) : null;
      const backendMessage = data.message ? String(data.message) : '';
      const status = error?.response?.status;
      const idPart = traceId ? ` (ID: ${traceId})` : '';

      let uiMessage = '';
      if (code === 'SMTP_AUTH_FAILED') {
        uiMessage = `Autenticazione email non riuscita. Verifica credenziali e impostazioni SMTP${idPart}`;
      } else if (code === 'SMTP_CONNECTION_FAILED') {
        uiMessage = `Server di posta non raggiungibile o in timeout. Riprova${idPart}`;
      } else if (code === 'SMTP_RECIPIENT_REJECTED') {
        uiMessage = `Destinatario non valido o rifiutato dal server di posta${idPart}`;
      } else if (backendMessage) {
        uiMessage = `${backendMessage}${idPart}`;
      } else if (status) {
        uiMessage = `Errore ${status} durante l'invio dell'email${idPart}`;
      } else {
        uiMessage = `Errore durante l'invio dell'email${idPart}`;
      }
      toast.error(uiMessage);
    } finally {
      setEmailSending(false);
    }
  };

  const fetchCustomerById = async (id) => {
    const sid = String(id || '').trim();
    if (!sid) return null;
    if (customerById[sid]) return customerById[sid];
    try {
      const res = await api.get(`/gtl-pratiche/customers/by-id/${sid}`);
      const c = res.data || null;
      if (c?.id != null) {
        setCustomerById((prev) => ({ ...prev, [String(c.id)]: c }));
        setCustomerOptions((prev) => {
          const merged = [...prev, c];
          const byId = new Map();
          merged.forEach((x) => {
            if (x?.id) byId.set(String(x.id), x);
          });
          return Array.from(byId.values());
        });
        if (!customerInputValue && String(formData.id_cliente || '').trim() === String(c.id)) {
          setCustomerInputValue(normalizeCustomerLabel(c));
        }
      }
      return c;
    } catch (_) {
      return null;
    }
  };

  useEffect(() => {
    const ids = Array.from(
      new Set(
        (rows || [])
          .map((r) => r?.id_cliente)
          .filter(Boolean)
          .map((x) => String(x))
      )
    );
    const missing = ids.filter((id) => !customerById[id]);
    if (missing.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      const chunkSize = 6;
      for (let i = 0; i < missing.length; i += chunkSize) {
        if (cancelled) return;
        const chunk = missing.slice(i, i + chunkSize);
        await Promise.all(chunk.map((id) => fetchCustomerById(id)));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rows]);

  useEffect(() => {
    const ids = Array.from(
      new Set(
        (rows || [])
          .map((r) => r?.id_contratto)
          .filter(Boolean)
          .map((x) => String(x))
      )
    );
    const missing = ids.filter((id) => !dossierById[id]);
    if (missing.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      const chunkSize = 6;
      for (let i = 0; i < missing.length; i += chunkSize) {
        if (cancelled) return;
        const chunk = missing.slice(i, i + chunkSize);
        await Promise.all(chunk.map((id) => fetchDossierById(id)));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rows]);

  useEffect(() => {
    if (!openDialog) return undefined;
    const id = String(formData.id_cliente || '').trim();
    if (id && !customerById[id]) {
      fetchCustomerById(id);
    }
    return undefined;
  }, [openDialog, formData.id_cliente, customerById]);

  const fetchDossierById = async (id) => {
    const sid = String(id || '').trim();
    if (!sid) return null;
    if (dossierById[sid]) return dossierById[sid];
    try {
      const res = await api.get(`/dossier/${sid}`);
      const d = res.data || null;
      if (d?.id != null) {
        setDossierById((prev) => ({ ...prev, [String(d.id)]: d }));
        setDossierOptions((prev) => {
          const merged = [...prev, d];
          const byId = new Map();
          merged.forEach((x) => {
            if (x?.id) byId.set(String(x.id), x);
          });
          return Array.from(byId.values());
        });
      }
      return d;
    } catch (_) {
      return null;
    }
  };

  useEffect(() => {
    const shouldLoad = Boolean(openDialog || csvImportOpen);
    if (!shouldLoad) {
      setMotivazioni([]);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setMotivazioniLoading(true);
      try {
        const res = await api.get('/recessi/motivazioni');
        const items = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) setMotivazioni(items);
      } catch (e) {
        if (!cancelled) setMotivazioni([]);
      } finally {
        if (!cancelled) setMotivazioniLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [openDialog, csvImportOpen]);

  useEffect(() => {
    if (!openDialog) {
      setCustomerOptions([]);
      setCustomerInputValue('');
      return undefined;
    }

    const term = String(customerInputValue || '').trim();
    const timeoutId = window.setTimeout(async () => {
      if (!term) {
        const currentId = String(formData.id_cliente || '').trim();
        const currentCustomer = currentId && customerById[currentId] ? [customerById[currentId]] : [];
        setCustomerOptions(currentCustomer);
        return;
      }

      setCustomerLoading(true);
      try {
        const response = await api.get('/gtl-pratiche/customers', {
          params: {
            search: term,
            limit: 20
          }
        });

        const options = Array.isArray(response.data) ? response.data : [];
        const currentId = String(formData.id_cliente || '').trim();
        const currentCustomer = currentId && customerById[currentId] ? [customerById[currentId]] : [];
        const merged = [...currentCustomer, ...options];
        const byId = new Map();
        merged.forEach((item) => {
          if (item?.id) byId.set(String(item.id), item);
        });
        const normalized = Array.from(byId.values());
        setCustomerOptions(normalized);
        setCustomerById((prev) => {
          const next = { ...prev };
          normalized.forEach((c) => {
            if (c?.id != null) next[String(c.id)] = c;
          });
          return next;
        });
      } catch (err) {
        console.error('Errore lookup clienti (recessi):', err);
      } finally {
        setCustomerLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [customerInputValue, openDialog, formData.id_cliente, customerById]);

  const pickCustomerOption = (id) => {
    const sid = String(id || '').trim();
    if (!sid) return null;
    const found = customerOptions.find((c) => String(c?.id) === sid) || null;
    if (found) return found;
    return customerById[sid] || null;
  };

  useEffect(() => {
    if (!openDialog) {
      setDossierOptions([]);
      setDossierInputValue('');
      return undefined;
    }

    const customerId = String(formData.id_cliente || '').trim();
    if (!customerId) {
      setDossierOptions([]);
      setDossierInputValue('');
      return undefined;
    }

    const term = String(dossierInputValue || '').trim();
    const timeoutId = window.setTimeout(async () => {
      setDossierLoading(true);
      try {
        const response = await api.get(`/dossier/customer/${customerId}`, {
          params: {
            search: term || undefined,
            limit: 20
          }
        });

        const options = Array.isArray(response.data) ? response.data : [];
        const currentId = String(formData.id_contratto || '').trim();
        const current = currentId && dossierById[currentId] ? [dossierById[currentId]] : [];
        const merged = [...current, ...options];
        const byId = new Map();
        merged.forEach((item) => {
          if (item?.id) byId.set(String(item.id), item);
        });
        const normalized = Array.from(byId.values());
        setDossierOptions(normalized);
        setDossierById((prev) => {
          const next = { ...prev };
          normalized.forEach((d) => {
            if (d?.id != null) next[String(d.id)] = d;
          });
          return next;
        });
      } catch (_) {
        setDossierOptions([]);
      } finally {
        setDossierLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [openDialog, formData.id_cliente, dossierInputValue, formData.id_contratto, dossierById]);

  useEffect(() => {
    if (!openDialog) return undefined;
    const id = String(formData.id_contratto || '').trim();
    if (id && !dossierById[id]) {
      fetchDossierById(id);
    }
    return undefined;
  }, [openDialog, formData.id_contratto, dossierById]);

  const pickDossierOption = (id) => {
    const sid = String(id || '').trim();
    if (!sid) return null;
    const found = dossierOptions.find((d) => String(d?.id) === sid) || null;
    if (found) return found;
    return dossierById[sid] || null;
  };

  useEffect(() => {
    if (!openDialog) return;
    if (String(formData.tipo_iniziativa || '').trim() !== 'cliente') return;
    if (clientePreavvisoTouched) return;
    const contrattoId = String(formData.id_contratto || '').trim();
    if (!contrattoId) return;
    const dossier = dossierOptions.find((d) => String(d?.id) === contrattoId) || dossierById[contrattoId] || null;
    const conferimentoIso = toIsoDateOnly(dossier?.data_conferimento);
    if (!conferimentoIso) return;
    const suggested = addDaysIso(conferimentoIso, 14);
    if (!suggested) return;
    setFormData((p) => {
      if (String(p?.tipo_iniziativa || '').trim() !== 'cliente') return p;
      if (String(p?.id_contratto || '').trim() !== contrattoId) return p;
      const current = String(p?.cliente?.data_scadenza_preavviso || '').trim();
      if (current === suggested) return p;
      return {
        ...p,
        cliente: { ...(p.cliente || {}), data_scadenza_preavviso: suggested }
      };
    });
  }, [clientePreavvisoTouched, dossierById, dossierOptions, formData.id_contratto, formData.tipo_iniziativa, openDialog]);

  const widgets = useMemo(() => {
    const w = widgetsData?.widgets || null;
    if (!w) return null;

    const importi = (rows || []).reduce(
      (acc, r) => {
        const d = dossierById[String(r?.id_contratto || '')] || null;
        acc.importo_totale += Number(d?.importo_totale || 0);
        acc.acconto += Number(d?.acconto || 0);
        return acc;
      },
      { importo_totale: 0, acconto: 0 }
    );

    const apertiCur = Number(w.aperti?.cur || 0);
    const apertiPrev = w.aperti?.prev === null || w.aperti?.prev === undefined ? null : Number(w.aperti.prev || 0);

    const tardiviCur = Number(w.tardivi_cliente?.cur || 0);
    const tardiviCurTot = Number(w.tardivi_cliente?.cur_tot || 0);
    const tardiviPrev = w.tardivi_cliente?.prev === null || w.tardivi_cliente?.prev === undefined ? null : Number(w.tardivi_cliente.prev || 0);
    const tardiviPrevTot =
      w.tardivi_cliente?.prev_tot === null || w.tardivi_cliente?.prev_tot === undefined ? null : Number(w.tardivi_cliente.prev_tot || 0);

    const rinunceCur = Number(w.rinunce_studio?.cur || 0);
    const rinuncePrev = w.rinunce_studio?.prev === null || w.rinunce_studio?.prev === undefined ? null : Number(w.rinunce_studio.prev || 0);

    const buildSubtitle = (cur, prev) => {
      if (prev === null) return null;
      const deltaAbs = cur - prev;
      const deltaPct = prev > 0 ? (deltaAbs / prev) * 100 : null;
      return buildVsPrevSubtitle(deltaAbs, deltaPct);
    };

    return [
      {
        key: 'aperti',
        title: 'Recessi aperti',
        value: formatNumberIt(apertiCur),
        subtitle: buildSubtitle(apertiCur, apertiPrev),
        icon: Timer,
        accent: UI_COLORS.indigo
      },
      {
        key: 'tardivi',
        title: 'Recessi tardivi (cliente)',
        value: `${formatNumberIt(tardiviCur)} tardivi su ${formatNumberIt(tardiviCurTot)}`,
        subtitle: buildSubtitle(tardiviCur, tardiviPrev),
        icon: AlertTriangle,
        accent: UI_COLORS.amber,
        extra: {
          prevValue: tardiviPrev !== null && tardiviPrevTot !== null ? `${formatNumberIt(tardiviPrev)}/${formatNumberIt(tardiviPrevTot)}` : null
        }
      },
      {
        key: 'rinunce',
        title: 'Rinunce società/studio',
        value: formatNumberIt(rinunceCur),
        subtitle: buildSubtitle(rinunceCur, rinuncePrev),
        icon: Scale,
        accent: UI_COLORS.sky
      },
      {
        key: 'importi',
        title: 'Importi (dossier)',
        value: formatMoneyIt(importi.importo_totale),
        subtitle: `Acconti: ${formatMoneyIt(importi.acconto)}`,
        icon: Wallet,
        accent: UI_COLORS.red,
        valueColor: theme.palette.error.main
      }
    ];
  }, [widgetsData, rows, dossierById, theme]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredRows = useMemo(() => rows || [], [rows]);

  const sortedRows = useMemo(() => {
    const stabilizedThis = filteredRows.map((el, index) => [el, index]);
    const pickSortValue = (row) => {
      const dossier = dossierById[String(row?.id_contratto || '')] || null;
      if (orderBy === 'importo_totale') return Number(dossier?.importo_totale || 0);
      if (orderBy === 'acconto') return Number(dossier?.acconto || 0);
      if (orderBy === 'data_conferimento') {
        const d = dossier?.data_conferimento ? new Date(dossier.data_conferimento) : null;
        const t = d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
        return t;
      }
      if (orderBy === 'responsabili') {
        const comm = String(dossier?.responsabile_commerciale || '');
        const upsell = String(dossier?.responsabile_upsell || '');
        const cont = String(dossier?.responsabile_contabile || '');
        return `${comm}|${upsell}|${cont}`;
      }
      if (orderBy === 'dossier_reference') return String(dossier?.reference || '');
      return row?.[orderBy];
    };
    const comparator = (a, b) => {
      const va = pickSortValue(a[0]);
      const vb = pickSortValue(b[0]);
      const isNumber = ['importo_totale', 'acconto', 'data_conferimento'].includes(orderBy) || typeof va === 'number' || typeof vb === 'number';
      const na = isNumber ? Number(va || 0) : String(va || '');
      const nb = isNumber ? Number(vb || 0) : String(vb || '');
      if (nb < na) return order === 'asc' ? 1 : -1;
      if (nb > na) return order === 'asc' ? -1 : 1;
      return a[1] - b[1];
    };
    stabilizedThis.sort(comparator);
    return stabilizedThis.map((el) => el[0]);
  }, [filteredRows, order, orderBy, dossierById]);

  const paginatedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  const openCreate = () => {
    setFormData({
      id_contratto: '',
      id_cliente: '',
      tipo_iniziativa: 'cliente',
      note: '',
      cliente: {
        data_comunicazione_recesso: '',
        data_scadenza_preavviso: '',
        canale_comunicazione: 'pec',
        id_motivazione: '',
        motivazione_dettaglio: ''
      },
      studio: {
        modalita: 'revoca',
        data_comunicazione_studio: '',
        data_preavviso_cliente: '',
        id_motivazione: '',
        motivazione_dettaglio: ''
      }
    });
    setClientePreavvisoTouched(false);
    setCustomerOptions([]);
    setCustomerInputValue('');
    setDossierOptions([]);
    setDossierInputValue('');
    setOpenDialog(true);
  };

  const openCsvPicker = () => {
    if (csvInputRef.current) {
      csvInputRef.current.value = '';
      csvInputRef.current.click();
    }
  };

  const openCsvInfoDialog = () => {
    if (!isAdmin) return;
    setCsvInfoOpen(true);
  };

  const closeCsvInfoDialog = () => {
    setCsvInfoOpen(false);
  };

  const openCsvImportDialog = () => {
    setCsvImportFile(null);
    setCsvImportTipo('cliente');
    setCsvImportMotivazioneId('');
    setCsvImportModalitaStudio('revoca');
    setCsvImportOpen(true);
  };

  const closeCsvImportDialog = () => {
    setCsvImportOpen(false);
  };

  const applyWidgetFilter = (key) => {
    if (key === 'aperti') {
      setFilterStato('aperto');
      setPage(0);
      return;
    }
    if (key === 'tardivi') {
      setFilterIniziativa('cliente');
      setPage(0);
      return;
    }
    if (key === 'rinunce') {
      setFilterIniziativa('studio');
      setPage(0);
      return;
    }
    if (key === 'importi') {
      setOrderBy('importo_totale');
      setOrder('desc');
    }
  };

  const continueCsvInfo = () => {
    setCsvInfoOpen(false);
    window.setTimeout(() => openCsvImportDialog(), 50);
  };

  const handleCsvSelected = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    setCsvImportFile(file);
  };

  const startCsvImportJob = async () => {
    if (!csvImportFile || !csvImportTipo || !csvImportMotivazioneId) return;
    if (csvImportTipo === 'studio' && !csvImportModalitaStudio) return;
    const form = new FormData();
    form.append('file', csvImportFile);
    form.append('tipo_iniziativa', String(csvImportTipo));
    form.append('motivazione_id', String(csvImportMotivazioneId));
    if (csvImportTipo === 'studio') {
      form.append('modalita_studio', String(csvImportModalitaStudio));
    }
    const res = await api.post('/recessi/import-csv', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    const jobId = res.data?.jobId;
    if (!jobId) {
      throw new Error('Job ID non ricevuto');
    }
    addJob(jobId, 'recessi_import_csv');
  };

  const closeDialog = () => {
    if (saving) return;
    setOpenDialog(false);
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const submitForm = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const tipo_iniziativa = String(formData.tipo_iniziativa || '').trim();
      const id_contratto = formData.id_contratto ? Number(formData.id_contratto) : null;
      const id_cliente = formData.id_cliente ? Number(formData.id_cliente) : null;

      const cliente = formData.cliente || {};
      const studio = formData.studio || {};
      const data_apertura =
        tipo_iniziativa === 'cliente'
          ? String(cliente.data_comunicazione_recesso || '').trim()
          : String(studio.data_comunicazione_studio || '').trim();

      if (!tipo_iniziativa || !data_apertura) {
        toast.error('Compila i campi obbligatori (iniziativa, data comunicazione)');
        return;
      }

      const payload = {
        id_contratto,
        id_cliente,
        tipo_iniziativa,
        data_apertura,
        note: formData.note ? String(formData.note).trim() : null,
        cliente:
          tipo_iniziativa === 'cliente'
            ? {
                data_comunicazione_recesso: cliente.data_comunicazione_recesso || null,
                data_scadenza_preavviso: cliente.data_scadenza_preavviso || null,
                canale_comunicazione: cliente.canale_comunicazione || null,
                id_motivazione: cliente.id_motivazione ? Number(cliente.id_motivazione) : null,
                motivazione_dettaglio: cliente.motivazione_dettaglio ? String(cliente.motivazione_dettaglio).trim() : null
              }
            : undefined,
        studio:
          tipo_iniziativa === 'studio'
            ? {
                modalita: studio.modalita || null,
                data_comunicazione_studio: studio.data_comunicazione_studio || null,
                data_preavviso_cliente: studio.data_preavviso_cliente || null,
                id_motivazione: studio.id_motivazione ? Number(studio.id_motivazione) : null,
                motivazione_dettaglio: studio.motivazione_dettaglio ? String(studio.motivazione_dettaglio).trim() : null
              }
            : undefined
      };

      const res = await api.post('/recessi/open', payload);
      const id = res.data?.id_recesso;
      toast.success('Recesso aperto');
      setOpenDialog(false);
      await fetchRows();
      if (id) navigate(`/recessi/${id}`);
    } catch (err) {
      console.error('Error saving recesso:', err);
      toast.error(err?.response?.data?.message || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (deleteLoading || !deleteTarget?.id_recesso) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/recessi/${deleteTarget.id_recesso}`);
      toast.success('Recesso eliminato');
      closeDeleteDialog();
      await fetchRows();
    } catch (err) {
      console.error('Error deleting recesso:', err);
      toast.error(err?.response?.data?.message || 'Errore durante l’eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) return prev.filter((c) => c !== key);
      return [...prev, key];
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Dialog
        open={accessDialogOpen}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 8
          }
        }}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleAccessCancel();
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 'bold' }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: '#fee2e2',
              color: '#b91c1c',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <AlertTriangle size={18} />
          </Box>
          Conferma gestione recessi
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {`Stai per accedere alla gestione dei recessi. Questa sezione può contenere informazioni sensibili (nominativi, recapiti, importi e note interne).\n\nAssicurati di avere un motivo legittimo e l’autorizzazione necessaria, in conformità alla normativa privacy e alle policy aziendali.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="ghost" onClick={handleAccessCancel}>
            Annulla
          </Button>
          <Button onClick={handleAccessConfirm}>Conferma</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <input ref={csvInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleCsvSelected} />
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: `${UI_COLORS.indigo}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ScrollText size={20} color={UI_COLORS.indigo} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Recessi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Elenco recessi
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={isAdmin ? 'Carica CSV' : 'Solo admin'}>
            <span>
              <Button variant="outline" onClick={openCsvInfoDialog} disabled={!isAdmin} title="Carica CSV">
                <Upload className="h-4 w-4" />
                Carica CSV
              </Button>
            </span>
          </Tooltip>
          <Button variant="outline" onClick={fetchRows} disabled={loading} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={openCreate}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuovo
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))'
          },
          gap: 2,
          mb: 3,
          width: '100%',
          maxWidth: '100%'
        }}
      >
        {(widgetsLoading || !widgets
          ? [{ key: 'w1' }, { key: 'w2' }, { key: 'w3' }, { key: 'w4' }]
          : widgets
        ).map((w, idx) => (
          <Box key={w.key || idx} sx={{ minWidth: 0 }}>
            {widgetsLoading || !widgets ? (
              <Paper
                elevation={0}
                variant="outlined"
                sx={{ p: 2, minHeight: 84, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <CircularProgress size={24} />
              </Paper>
            ) : (
              <StatWidget
                title={w.title}
                value={w.value}
                subtitle={w.subtitle?.text || ''}
                subtitleColor={w.subtitle?.color || undefined}
                icon={w.icon}
                accent={w.accent}
                onClick={() => applyWidgetFilter(w.key)}
              />
            )}
          </Box>
        ))}
      </Box>

      {accessGranted ? (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {filterText ? (
            <Chip
              label={`Ricerca: ${filterText}`}
              color="success"
              variant="outlined"
              onDelete={() => setFilterText('')}
              sx={{ fontWeight: 700 }}
            />
          ) : null}
          {filterStato ? (
            <Chip
              label={`Stato: ${translateStato(filterStato)}`}
              color="success"
              variant="outlined"
              onDelete={() => setFilterStato('')}
              sx={{ fontWeight: 700 }}
            />
          ) : null}
          {filterIniziativa ? (
            <Chip
              label={`Iniziativa: ${translateIniziativa(filterIniziativa)}`}
              color="success"
              variant="outlined"
              onDelete={() => setFilterIniziativa('')}
              sx={{ fontWeight: 700 }}
            />
          ) : null}
          {filterOrigine ? (
            <Chip
              label={`Origine: ${translateOrigineCreazione(filterOrigine)}`}
              color="success"
              variant="outlined"
              onDelete={() => setFilterOrigine('')}
              sx={{ fontWeight: 700 }}
            />
          ) : null}
          {dateFrom || dateTo ? (
            <Chip
              label={`Periodo: ${dateFrom ? formatDateIt(dateFrom) : '—'} → ${dateTo ? formatDateIt(dateTo) : '—'}`}
              color="success"
              variant="outlined"
              onDelete={() => {
                setDateFrom('');
                setDateTo('');
              }}
              sx={{ fontWeight: 700 }}
            />
          ) : null}
          {filterText || filterStato || filterIniziativa || filterOrigine || dateFrom || dateTo ? (
            <Chip
              label="Pulisci filtri"
              variant="filled"
              onClick={() => {
                setFilterText('');
                setFilterStato('');
                setFilterIniziativa('');
                setFilterOrigine('');
                setDateFrom('');
                setDateTo('');
                setPage(0);
              }}
              sx={{ fontWeight: 800, bgcolor: alpha(theme.palette.text.primary, 0.07) }}
            />
          ) : null}
        </Box>
      ) : null}

      <StyledCard>
        <CardContent>
          <Grid
            container
            spacing={2}
            sx={{
              mb: 3,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              '&::-webkit-scrollbar': { height: 6 }
            }}
          >
            <Grid item sx={{ minWidth: 340, flex: '0 0 auto' }}>
              <TextField
                size="small"
                fullWidth
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="Cerca (ID, dossier, cliente, note...)"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item sx={{ minWidth: 160, flex: '0 0 auto' }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Stato</InputLabel>
                <Select label="Stato" value={filterStato} onChange={(e) => setFilterStato(String(e.target.value || ''))}>
                  <MenuItem value="">Tutti</MenuItem>
                  <MenuItem value="aperto">Aperto</MenuItem>
                  <MenuItem value="in_lavorazione">In lavorazione</MenuItem>
                  <MenuItem value="chiuso">Chiuso</MenuItem>
                  <MenuItem value="annullato">Annullato</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item sx={{ minWidth: 170, flex: '0 0 auto' }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Iniziativa</InputLabel>
                <Select
                  label="Iniziativa"
                  value={filterIniziativa}
                  onChange={(e) => setFilterIniziativa(String(e.target.value || ''))}
                >
                  <MenuItem value="">Tutte</MenuItem>
                  <MenuItem value="cliente">Cliente</MenuItem>
                  <MenuItem value="studio">Società/Studio</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item sx={{ minWidth: 170, flex: '0 0 auto' }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Origine</InputLabel>
                <Select label="Origine" value={filterOrigine} onChange={(e) => setFilterOrigine(String(e.target.value || ''))}>
                  <MenuItem value="">Tutte</MenuItem>
                  <MenuItem value="manuale">Manuale</MenuItem>
                  <MenuItem value="import_csv">Import CSV</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item sx={{ minWidth: 170, flex: '0 0 auto' }}>
              <TextField
                size="small"
                type="date"
                label="Da"
                value={dateFrom}
                onChange={(e) => {
                  const next = String(e.target.value || '');
                  setDateFrom(next);
                  if (next && dateTo && next > dateTo) setDateTo(next);
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item sx={{ minWidth: 170, flex: '0 0 auto' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  type="date"
                  label="A"
                  value={dateTo}
                  onChange={(e) => {
                    const next = String(e.target.value || '');
                    setDateTo(next);
                    if (next && dateFrom && dateFrom > next) setDateFrom(next);
                  }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <IconButton size="small" onClick={(e) => setColumnsMenuAnchor(e.currentTarget)} title="Colonne">
                  <Columns2 size={18} />
                </IconButton>
                <Menu
                  anchorEl={columnsMenuAnchor}
                  open={Boolean(columnsMenuAnchor)}
                  onClose={() => setColumnsMenuAnchor(null)}
                  PaperProps={{ sx: { p: 1, borderRadius: 2 } }}
                >
                  {columnDefs.map((col) => (
                    <MenuItem key={col.key} onClick={() => toggleColumn(col.key)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: 1,
                            border: `1px solid ${alpha(theme.palette.text.primary, 0.25)}`,
                            bgcolor: visibleColumns.includes(col.key) ? alpha(theme.palette.primary.main, 0.2) : 'transparent'
                          }}
                        />
                        <Typography sx={{ fontSize: 13 }}>{col.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columnDefs
                    .filter((c) => visibleColumns.includes(c.key))
                    .map((col) => (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}
                      >
                        {col.sortable ? (
                          <TableSortLabel
                            active={orderBy === col.key}
                            direction={orderBy === col.key ? order : 'asc'}
                            onClick={() => handleRequestSort(col.key)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    Azioni
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4, fontSize: 13 }}>
                      Nessun dato
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row) => (
                    <TableRow key={row.id_recesso} hover>
                          {visibleColumns.includes('data_apertura') ? (
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {formatDateIt(row.data_apertura)}
                            </TableCell>
                          ) : null}
                          {visibleColumns.includes('stato') ? (
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <StatoChip stato={row.stato} />
                            </TableCell>
                          ) : null}
                          {visibleColumns.includes('tipo_iniziativa') ? (
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <IniziativaChip value={row.tipo_iniziativa} />
                            </TableCell>
                          ) : null}
                          {visibleColumns.includes('motivazione_descrizione') ? (
                            <TableCell sx={{ minWidth: 220 }}>
                              <MotivazioneChip value={row.motivazione_descrizione} />
                            </TableCell>
                          ) : null}
                          {visibleColumns.includes('id_cliente') ? (
                            <TableCell align="left" sx={{ minWidth: 220 }}>
                              {row.id_cliente ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                    {normalizeCustomerLabel(customerById[String(row.id_cliente)] || null) || `#${row.id_cliente}`}
                                  </Typography>
                                  {customerById[String(row.id_cliente)]?.email ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                      <MailIcon size={14} />
                                      <Typography variant="body2" color="text.secondary" noWrap>
                                        {customerById[String(row.id_cliente)].email}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {customerById[String(row.id_cliente)]?.telephone ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                      <PhoneIcon size={14} />
                                      <Typography variant="body2" color="text.secondary" noWrap>
                                        {customerById[String(row.id_cliente)].telephone}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                </Box>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          ) : null}
                      {visibleColumns.includes('data_conferimento') ? (
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {row.id_contratto && dossierById[String(row.id_contratto)]?.data_conferimento
                            ? formatDateTimeIt(dossierById[String(row.id_contratto)].data_conferimento)
                            : '-'}
                        </TableCell>
                      ) : null}
                      {visibleColumns.includes('importo_totale') ? (
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.error.main }} noWrap>
                            {row.id_contratto ? formatMoneyIt(dossierById[String(row.id_contratto)]?.importo_totale) : '-'}
                              </Typography>
                            </TableCell>
                          ) : null}
                      {visibleColumns.includes('acconto') ? (
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }} color="text.secondary" noWrap>
                            {row.id_contratto ? formatMoneyIt(dossierById[String(row.id_contratto)]?.acconto) : '-'}
                          </Typography>
                        </TableCell>
                      ) : null}
                      {visibleColumns.includes('responsabili') ? (
                        <TableCell sx={{ minWidth: 240 }}>
                          {row.id_contratto ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }} noWrap>
                                  Commerciale
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                  {dossierById[String(row.id_contratto)]?.responsabile_commerciale || '-'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }} noWrap>
                                  Upsell
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                  {dossierById[String(row.id_contratto)]?.responsabile_upsell || '-'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }} noWrap>
                                  Contabile
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                  {dossierById[String(row.id_contratto)]?.responsabile_contabile || '-'}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      ) : null}
                      {visibleColumns.includes('dossier_reference') ? (
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {row.id_contratto ? dossierById[String(row.id_contratto)]?.reference || `#${Number(row.id_contratto)}` : '-'}
                        </TableCell>
                      ) : null}
                          {visibleColumns.includes('creato_da_name') ? (
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.creato_da_name || '-'}</TableCell>
                          ) : null}
                          {visibleColumns.includes('note') ? (
                            <TableCell sx={{ minWidth: 220 }}>
                              <Typography variant="body2" noWrap title={row.note || ''}>
                                {row.note || '-'}
                              </Typography>
                            </TableCell>
                          ) : null}
                          {visibleColumns.includes('origine_creazione') ? (
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <OrigineChip value={row.origine_creazione} />
                            </TableCell>
                          ) : null}

                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <IconButton
                              size="small"
                              onClick={() => openEmailDialog(row)}
                              title={
                                isValidEmail(customerById[String(row.id_cliente)]?.email)
                                  ? 'Invia email'
                                  : 'Email cliente non disponibile'
                              }
                              disabled={!isValidEmail(customerById[String(row.id_cliente)]?.email)}
                            >
                              <MailIcon size={18} />
                            </IconButton>
                            <IconButton size="small" onClick={() => navigate(`/recessi/${row.id_recesso}`)} title="Apri dettaglio">
                              <EditIcon size={18} />
                            </IconButton>
                            <IconButton size="small" onClick={() => openDeleteDialog(row)} title="Elimina" sx={{ color: UI_COLORS.red }}>
                              <DeleteIcon size={18} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={sortedRows.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Righe per pagina"
          />
        </CardContent>
      </StyledCard>

      <Dialog open={csvInfoOpen} onClose={closeCsvInfoDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#eef2ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Upload size={20} color="#4f46e5" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Carica CSV
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Import massivo recessi da file CSV
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Il CSV deve contenere i campi: <b>codice fiscale</b>, <b>data recesso</b> (formato gg/mm/aaaa o yyyy-mm-dd).
          </Typography>

          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '0 0 auto'
                }}
              >
                <FileUp size={20} color="#2563eb" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Selezione file
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Seleziona il CSV con l’elenco dei recessi. Ogni riga rappresenta un recesso.
                </Typography>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '0 0 auto'
                }}
              >
                <CheckCircle2 size={20} color="#16a34a" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Validazione
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Viene verificato il formato date e il cliente viene identificato tramite codice fiscale.
                </Typography>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#f3e8ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '0 0 auto'
                }}
              >
                <ShieldCheck size={20} color="#7c3aed" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Import controllato
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  I recessi vengono creati solo se la riga è valida; in caso di errori l’import si ferma e viene mostrato l’esito.
                </Typography>
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outline" onClick={closeCsvInfoDialog}>
            Annulla
          </Button>
          <Button onClick={continueCsvInfo}>Continua</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={csvImportOpen} onClose={closeCsvImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#eef2ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Upload size={20} color="#4f46e5" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Import CSV
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Seleziona file e parametri di import
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 2
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                File CSV
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {csvImportFile ? csvImportFile.name : 'Nessun file selezionato'}
              </Typography>
            </Box>
            <Button variant="outline" onClick={openCsvPicker}>
              Seleziona file
            </Button>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={csvImportTipo === 'studio' ? 4 : 6}>
              <FormControl fullWidth>
                <InputLabel>Tipo recesso *</InputLabel>
                <Select
                  label="Tipo recesso *"
                  value={csvImportTipo}
                  onChange={(e) => {
                    const v = String(e.target.value || '');
                    setCsvImportTipo(v);
                    setCsvImportMotivazioneId('');
                    setCsvImportModalitaStudio('revoca');
                  }}
                >
                  <MenuItem value="cliente">Cliente</MenuItem>
                  <MenuItem value="studio">Società/Studio</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {csvImportTipo === 'studio' ? (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Modalità *</InputLabel>
                  <Select
                    label="Modalità *"
                    value={csvImportModalitaStudio}
                    onChange={(e) => setCsvImportModalitaStudio(String(e.target.value || ''))}
                  >
                    <MenuItem value="revoca">Revoca</MenuItem>
                    <MenuItem value="rinuncia">Rinuncia</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            ) : null}
            <Grid item xs={12} sm={csvImportTipo === 'studio' ? 4 : 6}>
              <Autocomplete
                disablePortal
                loading={motivazioniLoading}
                options={motivazioni.filter((m) => {
                  const ambito = String(m?.ambito || '').toLowerCase();
                  if (ambito === 'entrambi') return true;
                  if (csvImportTipo === 'cliente') return ambito === 'cliente';
                  if (csvImportTipo === 'studio') return ambito === 'studio';
                  return false;
                })}
                getOptionLabel={(o) => String(o?.descrizione || '')}
                value={motivazioni.find((m) => String(m?.id_motivazione) === String(csvImportMotivazioneId || '')) || null}
                onChange={(_, v) => {
                  setCsvImportMotivazioneId(v?.id_motivazione ? String(v.id_motivazione) : '');
                }}
                isOptionEqualToValue={(a, b) => String(a?.id_motivazione) === String(b?.id_motivazione)}
                renderInput={(params) => <TextField {...params} label="Motivazione *" placeholder="Cerca motivazione..." />}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outline" onClick={closeCsvImportDialog}>
            Chiudi
          </Button>
          <Button
            onClick={async () => {
              if (!csvImportFile || !csvImportTipo || !csvImportMotivazioneId) return;
              if (csvImportTipo === 'studio' && !csvImportModalitaStudio) return;
              try {
                await startCsvImportJob();
                toast.success('Import avviato: l’esito sarà disponibile nelle notifiche');
                closeCsvImportDialog();
              } catch (e) {
                toast.error(e?.response?.data?.message || e?.message || 'Errore avvio import');
              }
            }}
            disabled={
              !csvImportFile ||
              !csvImportTipo ||
              !csvImportMotivazioneId ||
              (csvImportTipo === 'studio' && !csvImportModalitaStudio)
            }
          >
            Importa
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900 }}>Nuovo recesso</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={customerOptions}
                value={pickCustomerOption(formData.id_cliente)}
                loading={customerLoading}
                onChange={(_e, v) => {
                  const id = v?.id ? String(v.id) : '';
                  setFormData((p) => ({ ...p, id_cliente: id, id_contratto: '' }));
                  setClientePreavvisoTouched(false);
                  setDossierOptions([]);
                  setDossierInputValue('');
                  if (v?.id != null) {
                    setCustomerById((prev) => ({ ...prev, [String(v.id)]: v }));
                    setCustomerOptions((prev) => {
                      const merged = [...prev, v];
                      const byId = new Map();
                      merged.forEach((c) => {
                        if (c?.id) byId.set(String(c.id), c);
                      });
                      return Array.from(byId.values());
                    });
                    setCustomerInputValue(v.full_name || '');
                  } else {
                    setCustomerInputValue('');
                  }
                }}
                onInputChange={(_event, value) => setCustomerInputValue(value)}
                inputValue={customerInputValue}
                filterOptions={(options) => options}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                getOptionLabel={(option) => normalizeCustomerLabel(option)}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ py: 1, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: '#eef2ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: 'primary.main',
                        mt: 0.25
                      }}
                    >
                      <UserIcon size={18} />
                    </Box>
                    <Box sx={{ ml: 1.5, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {normalizeCustomerLabel(option)}
                      </Typography>
                      {option?.telephone ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                          <PhoneIcon size={14} />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {option.telephone}
                          </Typography>
                        </Box>
                      ) : null}
                      {option?.email ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                          <MailIcon size={14} />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {option.email}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente / pratica"
                    placeholder="Nome cliente..."
                    helperText={
                      pickCustomerOption(formData.id_cliente) ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          {pickCustomerOption(formData.id_cliente)?.telephone ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <PhoneIcon size={13} />
                              <Typography variant="caption" color="text.secondary">
                                {pickCustomerOption(formData.id_cliente).telephone}
                              </Typography>
                            </Box>
                          ) : null}
                          {pickCustomerOption(formData.id_cliente)?.email ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <MailIcon size={13} />
                              <Typography variant="caption" color="text.secondary">
                                {pickCustomerOption(formData.id_cliente).email}
                              </Typography>
                            </Box>
                          ) : null}
                        </Box>
                      ) : (
                        'Lookup remoto sulla tabella customer'
                      )
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                size="small"
                disabled={!String(formData.id_cliente || '').trim()}
                options={dossierOptions}
                value={pickDossierOption(formData.id_contratto)}
                loading={dossierLoading}
                onChange={(_e, v) => {
                  const id = v?.id ? String(v.id) : '';
                  setFormData((p) => ({ ...p, id_contratto: id }));
                  setClientePreavvisoTouched(false);
                  if (v?.id != null) {
                    setDossierById((prev) => ({ ...prev, [String(v.id)]: v }));
                    setDossierOptions((prev) => {
                      const merged = [...prev, v];
                      const byId = new Map();
                      merged.forEach((d) => {
                        if (d?.id) byId.set(String(d.id), d);
                      });
                      return Array.from(byId.values());
                    });
                    setDossierInputValue(normalizeDossierLabel(v));
                  } else {
                    setDossierInputValue('');
                  }
                }}
                onInputChange={(_event, value) => setDossierInputValue(value)}
                inputValue={dossierInputValue}
                filterOptions={(options) => options}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                getOptionLabel={(option) => normalizeDossierLabel(option)}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ py: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                          {option.reference || `Dossier #${option.id}`}
                        </Typography>
                        {option.status ? (
                          <Chip
                            label={getDossierStatusLabel(option.status)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 800 }}
                          />
                        ) : null}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: UI_COLORS.green }}>
                          {formatMoneyIt(option.importo_totale)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                          Acconto: {formatMoneyIt(option.acconto)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                          {option.data_conferimento ? formatDateTimeIt(option.data_conferimento) : '-'}
                        </Typography>
                        {option.tipo_contratto ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                            {option.tipo_contratto}
                          </Typography>
                        ) : null}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800 }} noWrap>
                        {`Comm: ${option.responsabile_commerciale || '-'} · Upsell: ${option.responsabile_upsell || '-'} · Contabile: ${
                          option.responsabile_contabile || '-'
                        }`}
                      </Typography>
                      {option.description ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 520 }} noWrap>
                          {option.description}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Contratto (dossier)"
                    placeholder={String(formData.id_cliente || '').trim() ? 'Cerca per reference o ID...' : 'Seleziona prima un cliente'}
                    helperText={
                      pickDossierOption(formData.id_contratto) ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                              {formatMoneyIt(pickDossierOption(formData.id_contratto)?.importo_totale)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                              Acconto: {formatMoneyIt(pickDossierOption(formData.id_contratto)?.acconto)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                              {pickDossierOption(formData.id_contratto)?.data_conferimento
                                ? formatDateTimeIt(pickDossierOption(formData.id_contratto).data_conferimento)
                                : '-'}
                            </Typography>
                            {pickDossierOption(formData.id_contratto)?.status ? (
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                                {getDossierStatusLabel(pickDossierOption(formData.id_contratto).status)}
                              </Typography>
                            ) : null}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }} noWrap>
                            {`Comm: ${pickDossierOption(formData.id_contratto)?.responsabile_commerciale || '-'} · Upsell: ${
                              pickDossierOption(formData.id_contratto)?.responsabile_upsell || '-'
                            } · Contabile: ${pickDossierOption(formData.id_contratto)?.responsabile_contabile || '-'}`}
                          </Typography>
                        </Box>
                      ) : (
                        'Lookup sulla tabella dossier (filtrato per cliente)'
                      )
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.secondary', mb: 1 }}>Tipo di iniziativa</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    variant="outlined"
                    onClick={() => setFormData((p) => ({ ...p, tipo_iniziativa: 'cliente' }))}
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      cursor: 'pointer',
                      borderColor: formData.tipo_iniziativa === 'cliente' ? UI_COLORS.green : alpha(theme.palette.divider, 0.8),
                      bgcolor: formData.tipo_iniziativa === 'cliente' ? alpha(UI_COLORS.green, 0.08) : 'transparent'
                    }}
                  >
                    <Typography sx={{ fontWeight: 900 }}>Ipotesi 1 · Recesso del cliente</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    variant="outlined"
                    onClick={() => setFormData((p) => ({ ...p, tipo_iniziativa: 'studio' }))}
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      cursor: 'pointer',
                      borderColor: formData.tipo_iniziativa === 'studio' ? UI_COLORS.amber : alpha(theme.palette.divider, 0.8),
                      bgcolor: formData.tipo_iniziativa === 'studio' ? alpha(UI_COLORS.amber, 0.08) : 'transparent'
                    }}
                  >
                    <Typography sx={{ fontWeight: 900 }}>Ipotesi 2 · Rinuncia società/studio</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            {formData.tipo_iniziativa === 'cliente' ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    type="date"
                    label="Data comunicazione recesso"
                    value={formData.cliente?.data_comunicazione_recesso || ''}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        cliente: { ...(p.cliente || {}), data_comunicazione_recesso: String(e.target.value || '') }
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    type="date"
                    label="Data scadenza preavviso"
                    value={formData.cliente?.data_scadenza_preavviso || ''}
                    onChange={(e) => {
                      setClientePreavvisoTouched(true);
                      setFormData((p) => ({
                        ...p,
                        cliente: { ...(p.cliente || {}), data_scadenza_preavviso: String(e.target.value || '') }
                      }));
                    }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Canale comunicazione</InputLabel>
                    <Select
                      label="Canale comunicazione"
                      value={formData.cliente?.canale_comunicazione || 'pec'}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          cliente: { ...(p.cliente || {}), canale_comunicazione: String(e.target.value || '') }
                        }))
                      }
                    >
                      <MenuItem value="pec">PEC</MenuItem>
                      <MenuItem value="raccomandata">Raccomandata</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="altro">Altro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    label="Esito tempistica"
                    value={
                      formData.cliente?.data_comunicazione_recesso && formData.cliente?.data_scadenza_preavviso
                        ? String(formData.cliente.data_comunicazione_recesso) <= String(formData.cliente.data_scadenza_preavviso)
                          ? 'Nei tempi'
                          : 'Tardivo'
                        : 'In attesa delle date'
                    }
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    size="small"
                    disabled={motivazioniLoading}
                    options={motivazioni
                      .filter((m) => ['cliente', 'entrambi'].includes(String(m?.ambito || '').toLowerCase()))
                      .sort((a, b) => Number(a?.ordine || 0) - Number(b?.ordine || 0))}
                    value={
                      motivazioni.find((m) => String(m?.id_motivazione) === String(formData.cliente?.id_motivazione || '')) || null
                    }
                    onChange={(_e, v) =>
                      setFormData((p) => ({
                        ...p,
                        cliente: { ...(p.cliente || {}), id_motivazione: v?.id_motivazione ? String(v.id_motivazione) : '' }
                      }))
                    }
                    getOptionLabel={(o) => String(o?.descrizione || '').trim() || ''}
                    isOptionEqualToValue={(a, b) => String(a?.id_motivazione) === String(b?.id_motivazione)}
                    renderInput={(params) => <TextField {...params} label="Motivazione" placeholder="Cerca motivazione..." />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    label="Dettaglio motivazione (facoltativo, non incide sui KPI)"
                    value={formData.cliente?.motivazione_dettaglio || ''}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        cliente: { ...(p.cliente || {}), motivazione_dettaglio: String(e.target.value || '') }
                      }))
                    }
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Note aggiuntive..."
                  />
                </Grid>
              </>
            ) : null}

            {formData.tipo_iniziativa === 'studio' ? (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Modalità</InputLabel>
                    <Select
                      label="Modalità"
                      value={formData.studio?.modalita || 'revoca'}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          studio: { ...(p.studio || {}), modalita: String(e.target.value || '') }
                        }))
                      }
                    >
                      <MenuItem value="revoca">Revoca del mandato</MenuItem>
                      <MenuItem value="rinuncia">Rinuncia al mandato</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    type="date"
                    label="Data comunicazione studio"
                    value={formData.studio?.data_comunicazione_studio || ''}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        studio: { ...(p.studio || {}), data_comunicazione_studio: String(e.target.value || '') }
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    type="date"
                    label="Data preavviso concesso al cliente"
                    value={formData.studio?.data_preavviso_cliente || ''}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        studio: { ...(p.studio || {}), data_preavviso_cliente: String(e.target.value || '') }
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    size="small"
                    disabled={motivazioniLoading}
                    options={motivazioni
                      .filter((m) => ['studio', 'entrambi'].includes(String(m?.ambito || '').toLowerCase()))
                      .sort((a, b) => Number(a?.ordine || 0) - Number(b?.ordine || 0))}
                    value={motivazioni.find((m) => String(m?.id_motivazione) === String(formData.studio?.id_motivazione || '')) || null}
                    onChange={(_e, v) =>
                      setFormData((p) => ({
                        ...p,
                        studio: { ...(p.studio || {}), id_motivazione: v?.id_motivazione ? String(v.id_motivazione) : '' }
                      }))
                    }
                    getOptionLabel={(o) => String(o?.descrizione || '').trim() || ''}
                    isOptionEqualToValue={(a, b) => String(a?.id_motivazione) === String(b?.id_motivazione)}
                    renderInput={(params) => <TextField {...params} label="Motivazione" placeholder="Cerca motivazione..." />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    label="Dettaglio motivazione (facoltativo, non incide sui KPI)"
                    value={formData.studio?.motivazione_dettaglio || ''}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        studio: { ...(p.studio || {}), motivazione_dettaglio: String(e.target.value || '') }
                      }))
                    }
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Note aggiuntive..."
                  />
                </Grid>
              </>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outline" onClick={closeDialog} disabled={saving}>
            Annulla
          </Button>
          <Button onClick={submitForm} disabled={saving}>
            {saving ? 'Apro...' : 'Apri recesso'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={emailDialogOpen}
        onClose={closeEmailDialog}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
                color: '#3b82f6'
              }}
            >
              <MailIcon size={20} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.15, fontSize: '1.1rem' }}>
                Invia email
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }} noWrap>
                Compila i campi e invia il riepilogo della comunicazione
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={emailAccounts}
                loading={emailAccountsLoading}
                value={emailAccounts.find((a) => String(a?.id) === String(emailAccountId)) || null}
                onChange={(_e, v) => setEmailAccountId(v?.id != null ? String(v.id) : '')}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                getOptionLabel={(o) => {
                  const label = String(o?.name || o?.email || '').trim();
                  const email = String(o?.email || '').trim();
                  if (!label && !email) return '';
                  if (label && email && label !== email) return `${label} <${email}>`;
                  return email || label;
                }}
                renderInput={(params) => <TextField {...params} label="Account mittente" placeholder="Seleziona account..." />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                label="A"
                value={emailForm.to}
                onChange={(e) => setEmailForm((p) => ({ ...p, to: String(e.target.value || '') }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                label="CC"
                value={emailForm.cc}
                onChange={(e) => setEmailForm((p) => ({ ...p, cc: String(e.target.value || '') }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                label="Oggetto"
                value={emailForm.subject}
                onChange={(e) => setEmailForm((p) => ({ ...p, subject: String(e.target.value || '') }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1, fontWeight: 500 }}>Messaggio</Typography>
              <Box sx={{ 
                '& .quill': { 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                  overflow: 'hidden'
                },
                '& .ql-toolbar': { 
                  border: 'none', 
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                  bgcolor: '#fff'
                },
                '& .ql-container': { 
                  border: 'none',
                  minHeight: '250px',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }
              }}>
                <ReactQuill 
                  theme="snow"
                  value={emailForm.body}
                  onChange={(content) => setEmailForm((p) => ({ ...p, body: content }))}
                  modules={{
                    toolbar: [
                      [{ 'font': [] }],
                      [{ 'size': [] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'script': 'sub'}, { 'script': 'super' }],
                      [{ 'align': [] }],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
                      ['blockquote', 'code-block'],
                      ['link', 'image'],
                      ['clean']
                    ]
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
          <Button 
            variant="ghost" 
            onClick={closeEmailDialog} 
            disabled={emailSending}
            style={{ fontWeight: 600, color: '#334155' }}
          >
            Annulla
          </Button>
          <Button 
            onClick={handleSendEmail} 
            disabled={emailSending}
            style={{ 
              backgroundColor: '#1e293b', 
              color: 'white', 
              fontWeight: 600,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 16px'
            }}
          >
            <MailIcon size={16} />
            {emailSending ? 'Invio...' : 'Invia'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                bgcolor: alpha(UI_COLORS.red, 0.12),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: UI_COLORS.red,
                flex: '0 0 auto'
              }}
            >
              <AlertCircle size={20} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }}>Conferma eliminazione</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }} noWrap>
                Recesso #{deleteTarget?.id_recesso || '-'} • {formatDateIt(deleteTarget?.data_apertura)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pt: 0 }}>
          <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, p: 1.75 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 800 }}>Stato</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatoChip stato={deleteTarget?.stato} />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 800 }}>Iniziativa</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <IniziativaChip value={deleteTarget?.tipo_iniziativa} />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 800 }}>Contratto (dossier)</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                  {deleteTarget?.id_contratto
                    ? dossierById[String(deleteTarget.id_contratto)]?.reference || `#${Number(deleteTarget.id_contratto)}`
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 800 }}>Cliente</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                  {deleteTarget?.id_cliente
                    ? normalizeCustomerLabel(customerById[String(deleteTarget.id_cliente)] || null) || `#${Number(deleteTarget.id_cliente)}`
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 800 }}>Nota</Typography>
                <Typography sx={{ fontSize: 13 }}>{deleteTarget?.note || '-'}</Typography>
              </Grid>
            </Grid>
          </Paper>
          <Typography sx={{ mt: 2, fontSize: 13, color: 'text.secondary' }}>
            Questa operazione è irreversibile. Verranno eliminati anche i dati collegati (relazione, documenti, storico) se presenti.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outline" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={confirmDelete} disabled={deleteLoading} style={{ backgroundColor: UI_COLORS.red, color: 'white' }}>
            {deleteLoading ? 'Elimino...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
